import { Option, Select, Box, Typography } from "@mui/joy";

import type { MaterialLineItemOption } from "../../../types";
import { ProductOptionContent, ProductSelectValue } from "./ProductOptionContent";
import {
    findOptionByKey,
    findOptionForProductId,
} from "../../../utils/materialColorSelection";

export type MaterialProductSelectProps = {
    products: MaterialLineItemOption[];
    /** Selected size option key, or a concrete ProductId (cart hydrate). */
    value: string | null;
    onChange?: (optionKey: string | null) => void;
    placeholder?: string;
    /** Field Shingles option shortlist (OptionKeys). */
    multiple?: boolean;
    multiValues?: string[];
    onMultiChange?: (optionKeys: string[]) => void;
    maxMulti?: number;
};

function resolveOptions(
    products: MaterialLineItemOption[],
    keys: string[],
): MaterialLineItemOption[] {
    return keys
        .map(
            (key) =>
                findOptionByKey(products, key) ||
                findOptionForProductId(products, key),
        )
        .filter(Boolean) as MaterialLineItemOption[];
}

export default function MaterialProductSelect({
    products,
    value,
    onChange,
    placeholder = "Select product…",
    multiple = false,
    multiValues = [],
    onMultiChange,
    maxMulti = 5,
}: MaterialProductSelectProps) {
    if (multiple) {
        const selectedOptions = resolveOptions(products, multiValues);
        const selectedKeys = selectedOptions.map((o) => o.OptionKey);

        return (
            <Select
                multiple
                size="sm"
                placeholder={placeholder}
                value={selectedKeys}
                onChange={(_, next) => {
                    const keys = (next as string[] | null) || [];
                    if (keys.length > maxMulti) return;
                    onMultiChange?.(keys);
                }}
                renderValue={() => {
                    if (selectedOptions.length === 0) return null;
                    if (selectedOptions.length === 1) {
                        return (
                            <ProductSelectValue product={selectedOptions[0]} />
                        );
                    }
                    return (
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                justifyContent: "center",
                                minWidth: 0,
                                width: "100%",
                                textAlign: "left",
                                py: 0.25,
                            }}
                        >
                            <Typography
                                level="body-sm"
                                sx={{
                                    display: "block",
                                    width: "100%",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    textAlign: "left",
                                }}
                            >
                                {selectedOptions
                                    .map((p) => p.ProductName)
                                    .join(", ")}
                            </Typography>
                            <Typography
                                level="body-xs"
                                color="neutral"
                                sx={{
                                    display: "block",
                                    width: "100%",
                                    textAlign: "left",
                                }}
                            >
                                {selectedOptions.length} estimate options
                            </Typography>
                        </Box>
                    );
                }}
                slotProps={{
                    listbox: {
                        sx: {
                            minWidth: 320,
                            "--ListItem-paddingY": "6px",
                        },
                    },
                    button: {
                        sx: {
                            justifyContent: "flex-start",
                            textAlign: "left",
                        },
                    },
                }}
                sx={{
                    width: "100%",
                    bgcolor: "background.surface",
                    borderRadius: "4px",
                    boxShadow: "none",
                    textAlign: "left",
                    "& .MuiSelect-button": {
                        justifyContent: "flex-start",
                        textAlign: "left",
                    },
                }}
            >
                {products.map((product) => (
                    <Option key={product.OptionKey} value={product.OptionKey}>
                        <ProductOptionContent product={product} />
                    </Option>
                ))}
            </Select>
        );
    }

    const selected =
        findOptionByKey(products, value) ||
        findOptionForProductId(products, value);

    return (
        <Select
            size="sm"
            placeholder={placeholder}
            value={selected?.OptionKey ?? null}
            onChange={(_, next) => onChange?.(next as string | null)}
            renderValue={() =>
                selected ? <ProductSelectValue product={selected} /> : null
            }
            slotProps={{
                listbox: {
                    sx: {
                        minWidth: 320,
                        "--ListItem-paddingY": "6px",
                    },
                },
            }}
            sx={{
                width: "100%",
                bgcolor: "background.surface",
                borderRadius: "4px",
                boxShadow: "none",
            }}
        >
            {products.map((product) => (
                <Option key={product.OptionKey} value={product.OptionKey}>
                    <ProductOptionContent product={product} />
                </Option>
            ))}
        </Select>
    );
}
